import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchMetaData, DEFAULT_META } from '../data/metaApi.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import { getExecSummaryOverride } from '../data/execSummaryOverride.js'
import { getCompanyUpdateOverride } from '../data/companyUpdateOverride.js'
import { getQuarterlyPrereadOverride } from '../data/quarterlyPrereadOverride.js'

/**
 * Top-level hook for the full meta object.
 *
 * Archive-aware: when the archiveViewStore is active, this synthesizes
 * a "meta as it looked that week" object from the snapshot + a handful
 * of always-live slices (aiPreferences, weeklyHistory, lastSnapshotWeek).
 * That way pillar components, the Executive Summary card, and other
 * meta-reading surfaces render the frozen values without any code
 * changes of their own.
 *
 * Live path (default):
 *   cache key ['meta'], 60s refetch interval as a safety net under
 *   Ably. refetchOnWindowFocus off — a GET within ~60s of a PUT can
 *   return stale KV data. staleTime keeps focus changes cheap.
 */
export function useMetaData() {
  const queryClient = useQueryClient()
  const { active, weekKey } = useArchiveViewStore((s) => ({
    active: s.active,
    weekKey: s.weekKey,
  }))

  const live = useQuery({
    queryKey: ['meta'],
    queryFn: fetchMetaData,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })

  if (!active) {
    let data = live.data || DEFAULT_META
    // If we just saved an exec summary or a Company Update, prefer
    // those authoritative values until the override TTLs expire.
    // Without this, a 60-second meta refetch can land on stale KV data
    // and silently revert the save.
    const execOverride = getExecSummaryOverride()
    if (execOverride !== undefined) {
      data = { ...data, execSummary: execOverride }
    }
    const companyOverride = getCompanyUpdateOverride()
    if (companyOverride !== undefined) {
      data = { ...data, companyUpdate: companyOverride }
    }
    const prereadOverride = getQuarterlyPrereadOverride()
    if (prereadOverride !== undefined) {
      data = { ...data, quarterlyPreread: prereadOverride }
    }
    return {
      ...live,
      data,
    }
  }

  // Build a frozen meta from the snapshot. Use the LIVE cache for
  // slices that aren't week-specific (aiPreferences, weeklyHistory) so
  // navigating between weeks keeps rendering the archive list and the
  // tour/banner state.
  const liveMeta = queryClient.getQueryData(['meta']) || DEFAULT_META
  const snapshot = liveMeta?.weeklyHistory?.[weekKey] || {}
  const archivedMeta = {
    ...DEFAULT_META,
    weeklySummary: snapshot.weeklySummary || {},
    teamUpdates: snapshot.teamUpdates || {},
    execSummary: snapshot.execSummary || null,
    companyUpdate: snapshot.companyUpdate || null,
    quarterlyPreread: snapshot.quarterlyPreread || null,
    actionItems: snapshot.actionItems || {},
    reactions: snapshot.reactions || {},
    fieldEdits: snapshot.fieldEdits || {},
    comments: Array.isArray(snapshot.comments) ? snapshot.comments : [],
    aiPreferences: liveMeta.aiPreferences || [],
    weeklyHistory: liveMeta.weeklyHistory || {},
    lastSnapshotWeek: liveMeta.lastSnapshotWeek || null,
    // Presence + DMs are real-time only — don't show stale ones in the
    // archive view.
    presence: {},
    dms: {},
  }

  return {
    data: archivedMeta,
    isLoading: false,
    isFetching: false,
    error: null,
    isSuccess: true,
  }
}
